import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import type { EventResponse, ReorderEventsRequest, TopicResponse } from '@lifetimeline/shared';
import { sortEvents } from '@lifetimeline/shared';

import type { ReactNode } from 'react';

import { eventPeriodText } from '../lib/eventPeriodText.js';
import { AutoLoad } from './AutoLoad.js';
import { useI18n } from '../i18n/index.js';
import styles from './Timeline.module.css';

interface TimelineProps {
  topics: readonly TopicResponse[];
  events: readonly EventResponse[];
  /** 고를 수 있는 주제. 누르면 그 주제만 남는다 */
  activeTopicId?: string | null;
  onEventClick?: (event: EventResponse) => void;
  /**
   * 끌어서 순서를 바꿨을 때. 주면 끌 수 있게 되고, 안 주면 읽기 전용이다
   * (남의 타임라인에서는 안 준다).
   */
  onReorder?: (years: ReorderEventsRequest['years']) => void;
  /**
   * 기록들 **바로 위**에 얹을 것. 기록 더하기가 여기 온다.
   *
   * 축 밖 멀리 두면 "이 타임라인에 더한다"가 아니라 화면 어딘가의 버튼이 된다.
   */
  before?: ReactNode;
  /** 있으면 축 끝에 "더 보기"가 붙는다. 없으면 다 불러온 것이다 */
  onLoadMore?: () => void;
  loadingMore?: boolean;
}

/**
 * 인생 타임라인.
 *
 * 세로축 하나에 사건을 **최근부터** 찍는다. 기존 Life Timeline의 모양을
 * 그대로 가져왔다 — 열었을 때 지금이 먼저 보여야 한다.
 *
 * 연도는 **바뀔 때만** 찍는다. 사건마다 붙이면 같은 해가 줄줄이 반복되면서
 * 정작 언제인지가 눈에 안 들어온다.
 *
 * 간격을 시간에 비례시키지 않고 **일정하게** 둔다. 비례시키면 20년 공백이
 * 화면 몇 개를 차지하고, 바쁘게 산 해는 서로 겹쳐 읽을 수가 없다.
 */
export function Timeline({
  topics,
  events,
  activeTopicId = null,
  onEventClick,
  onReorder,
  before,
  onLoadMore,
  loadingMore = false,
}: TimelineProps) {
  const { t } = useI18n();
  const colorOf = new Map(topics.map((topic) => [topic.topicId, topic.color]));
  const shown = sortEvents(
    activeTopicId === null ? events : events.filter((event) => event.topicId === activeTopicId),
  );

  /*
   * 걸러 보는 중에는 끌 수 없다. 화면에 그 해의 일부만 있는데 그 순서를
   * 저장하면 안 보이는 사건이 자리를 잃는다(서버도 같은 이유로 거절한다).
   */
  const canDrag = onReorder !== undefined && activeTopicId === null;

  const sensors = useSensors(
    // 칸을 누르면 수정 화면이 열린다. 8px은 움직여야 끌기로 친다
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      // Enter는 사건을 여는 데 쓴다 — 가져가면 키보드로는 수정할 수가 없다
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space'] },
    }),
  );

  /*
   * 기록이 없어도 **축은 남긴다.**
   *
   * 선까지 사라지면 화면에 아무 뼈대가 없어서, 여기가 무엇을 담는 자리인지
   * 안 보인다. 빈 축은 "여기에 쌓인다"는 자리 표시다.
   */
  if (shown.length === 0) {
    return (
      <ol className={[styles.timeline, styles.emptyAxis].join(' ')}>
        {before === undefined ? null : <li className={styles.bar}>{before}</li>}
      </ol>
    );
  }

  /**
   * 놓은 자리가 **연도까지** 정한다.
   *
   * 끌어다 놓은 자리에 있던 사건의 해를 따라간다. 해를 넘겨 끌면 나간 해와
   * 들어온 해가 같이 바뀌므로 둘 다 보낸다 — 한쪽만 보내면 나머지 한쪽의
   * 자리를 아무도 다시 세지 않아 번호가 비거나 겹친다.
   */
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over === null || active.id === over.id || onReorder === undefined) {
      return;
    }
    const from = shown.findIndex((event) => event.eventId === active.id);
    const to = shown.findIndex((event) => event.eventId === over.id);
    const fromYear = shown[from]?.year;
    const toYear = shown[to]?.year;
    if (from < 0 || to < 0 || fromYear === undefined || toYear === undefined) {
      return;
    }

    const movedId = String(active.id);
    const next = arrayMove([...shown], from, to);
    const yearOf = (event: EventResponse) => (event.eventId === movedId ? toYear : event.year);
    const touched = fromYear === toYear ? [toYear] : [toYear, fromYear];

    onReorder(
      touched.map((year) => ({
        year,
        eventIds: next.filter((event) => yearOf(event) === year).map((event) => event.eventId),
      })),
    );
  };

  const list = (
    <ol className={styles.timeline}>
      {before === undefined ? null : <li className={styles.bar}>{before}</li>}
      {shown.map((event, index) => (
        <Row
          key={event.eventId}
          event={event}
          showYear={event.year !== shown[index - 1]?.year}
          color={colorOf.get(event.topicId) ?? 'var(--line-strong)'}
          sortable={canDrag}
          {...(onEventClick === undefined ? {} : { onClick: () => onEventClick(event) })}
        />
      ))}

      {/*
        축 **끝**에서 스크롤이 닿으면 다음 쪽이 자동으로 이어진다. 버튼을
        누르게 하면 위에서 아래로 훑어 내려가는 흐름이 중간에 끊긴다.
      */}
      {onLoadMore === undefined ? null : (
        <li className={styles.more}>
          <AutoLoad onLoad={onLoadMore} loading={loadingMore} />
        </li>
      )}
    </ol>
  );

  if (!canDrag) {
    return list;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      /*
       * 세로로만 움직인다. 예전에는 `restrictToParentElement`였는데, 끄는
       * 대상이 줄 전체가 아니라 줄 **안쪽**이 되면서 그 부모가 한 줄짜리
       * 칸이 됐다 — 그대로 두면 한 줄 높이 밖으로 못 나간다.
       */
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      accessibility={{ screenReaderInstructions: { draggable: t('timeline.reorderHint') } }}
    >
      <SortableContext
        items={shown.map((event) => event.eventId)}
        strategy={verticalListSortingStrategy}
      >
        {list}
      </SortableContext>
    </DndContext>
  );
}

function Row({
  event,
  showYear,
  color,
  sortable,
  onClick,
}: {
  event: EventResponse;
  showYear: boolean;
  color: string;
  sortable: boolean;
  onClick?: () => void;
}) {
  const { t } = useI18n();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: event.eventId,
    disabled: !sortable,
  });

  const period = eventPeriodText(event, t);

  /*
   * 주소가 있으면 **제목이 곧 링크**다 — 색이 바뀌고 밑줄이 그어진다.
   *
   * ⚠️ 내 타임라인에서는 제목을 링크로 만들 수 없다. 줄 전체가 고치기
   * 버튼인데 버튼 안의 링크는 HTML로도 틀리고, 눌렀을 때 어느 쪽이 먹는지
   * 브라우저마다 다르다. 내 것에서는 고치기 화면에 주소가 그대로 있다.
   */
  const body = (
    <>
      {event.url !== undefined && onClick === undefined ? (
        <a
          className={`${styles.title} ${styles.titleLink}`}
          href={event.url}
          target="_blank"
          /*
           * `noreferrer`까지 붙인다 — 새 창이 `window.opener`로 이 페이지를
           * 건드릴 수 있고, 어디서 왔는지도 남의 서버에 줄 이유가 없다.
           */
          rel="noreferrer noopener"
        >
          {event.title}
        </a>
      ) : (
        /*
         * 내 타임라인에서는 링크로 만들 수 없지만(줄 전체가 고치기 버튼이다)
         * **주소가 걸렸다는 것은 보여야** 한다 — 안 그러면 어느 기록에 링크를
         * 달아 뒀는지 하나씩 열어 봐야 안다. 색과 밑줄은 같고, 누르면 고치기
         * 화면이 열려 거기서 주소를 본다.
         */
        <strong className={`${styles.title} ${event.url === undefined ? '' : styles.titleLinked}`}>
          {event.title}
        </strong>
      )}
      {/*
        기간은 **축이 아니라 글**이다. 점은 시작 연도에 그대로 찍히고,
        얼마나 걸렸는지는 여기서 한 줄로 말한다 — 축을 기간만큼 늘리면
        인생을 훑는 화면이 간트 차트가 된다.
      */}
      {period === null ? null : <span className={styles.period}>{period}</span>}
      {event.note === undefined ? null : <span className={styles.note}>{event.note}</span>}
    </>
  );

  /*
   * ⚠️ 끄는 것은 **줄 안쪽**이다. 연도는 바깥에 남는다.
   *
   * 예전에는 `<li>` 전체가 움직여서 연도가 사건을 따라다녔다. 연도는 그
   * 사건의 것이 아니라 **축의 눈금**이다 — 사건이 지나가도 자리를 지켜야
   * 어디로 옮겨지는 중인지가 보인다.
   */
  return (
    <li className={styles.row}>
      <span className={styles.year} data-shown={showYear}>
        {showYear ? event.year : ''}
      </span>
      <div
        ref={setNodeRef}
        className={styles.movable}
        /*
         * ⚠️ `CSS.Translate`다. `CSS.Transform`이 아니다.
         *
         * dnd-kit의 `transform`에는 **`scaleX`/`scaleY`가 들어 있다** —
         * 끌고 있는 줄과 지나가는 줄의 크기가 다르면 그 비율이 담긴다.
         * 여기 줄은 한 줄짜리도 있고 설명이 붙어 두 줄짜리도 있어서, 그대로
         * 쓰면 끌 때마다 글씨가 세로로 늘어났다 줄었다 하며 뭉개졌다.
         * 자리만 옮기면 되는 일이라 이동값만 쓴다.
         */
        style={{ transform: CSS.Translate.toString(transform), transition }}
        data-dragging={isDragging}
        {...(sortable ? attributes : {})}
        {...(sortable ? listeners : {})}
      >
        <span className={styles.dot} style={{ background: color }} aria-hidden="true" />
        {/*
          ⚠️ 링크는 **버튼 밖**에 있어야 한다. 내 타임라인에서는 줄 전체가
          고치기 버튼인데, 버튼 안에 링크를 넣으면 HTML로도 틀리고 누를 때
          어느 쪽이 먹는지도 브라우저마다 다르다.
        */}
        <div className={styles.stack}>
          {onClick === undefined ? (
            <span className={styles.body}>{body}</span>
          ) : (
            <button type="button" className={styles.body} onClick={onClick}>
              {body}
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
